<template>
  <div
    class="base-product-card"
    :class="{ 'has-colors': colors.length }"
    data-testid="product-card"
  >
    <div class="base-product-card__image-wrapper">
      <slot
        name="image"
        v-bind="{
          image,
          title,
          link,
          imageHeight,
          imageWidth,
          imageTag,
          nuxtImgConfig,
        }"
      >
        <BaseButton
          :link="link"
          class="sf-button--pure base-product-card__link"
          data-testid="product-link"
          aria-label="Go To Product"
          v-on="$listeners"
        >
          <template v-if="Array.isArray(image)">
            <BaseImage
              v-for="(picture, key) in image.slice(0, 2)"
              :key="key"
              class="base-product-card__picture"
              :src="picture"
              :alt="title"
              :fallback-image-src="fallbackImageSrc"
              :width="imageWidth"
              :height="imageHeight"
              :image-tag="imageTag"
              :nuxt-img-config="nuxtImgConfig"
            />
          </template>
          <BaseImage
            v-else
            class="base-product-card__image"
            :src="image"
            :alt="title"
            :width="imageWidth"
            :fallback-image-src="fallbackImageSrc"
            :height="imageHeight"
            :image-tag="imageTag"
            :nuxt-img-config="nuxtImgConfig"
          />
        </BaseButton>
      </slot>
      <slot name="badge" v-bind="{ badgeLabel, badgeColor }">
        <BaseBadge
          class="base-product-card__badge"
          :class="[badgeColorClass, { 'display-none': !badgeLabel }]"
          >{{ badgeLabel }}</BaseBadge
        >
      </slot>
      <div :class="{ 'display-none': !showAddToCartButton }">
        <slot
          name="add-to-cart"
          v-bind="{
            isAddedToCart,
            showAddedToCartBadge,
            isAddingToCart,
            title,
          }"
        >
          <BaseCircleIcon
            class="base-product-card__add-button"
            :aria-label="`Add to Cart ${title}`"
            :has-badge="showAddedToCartBadge"
            :disabled="addToCartDisabled"
            data-testid="product-add-icon"
            @click="onAddToCart"
          >
            <span class="base-product-card__add-button--icons">
              <transition v-if="!isAddingToCart && !isAddedToCart" name="sf-pulse" mode="out-in">
                <slot name="add-to-cart-icon">
                  <BaseIcon key="add_to_cart" icon="add_to_cart" size="20px" color="white" />
                </slot>
              </transition>
              <transition v-else name="sf-pulse" mode="out-in">
                <slot name="adding-to-cart-icon">
                  <BaseIcon key="added_to_cart" icon="added_to_cart" size="20px" color="white" />
                </slot>
              </transition>
            </span>
          </BaseCircleIcon>
        </slot>
      </div>
    </div>
    <slot name="title" v-bind="{ title, link }">
      <BaseButton
        :link="link"
        class="sf-button--pure base-product-card__link"
        data-testid="product-link"
        v-on="$listeners"
      >
        <span class="base-product-card__title">
          {{ title }}
        </span>
      </BaseButton>
    </slot>
    <slot name="price" v-bind="{ specialPrice, regularPrice }">
      <BasePrice
        :class="{ 'display-none': !regularPrice }"
        class="base-product-card__price"
        :regular="regularPrice"
        :special="specialPrice"
      />
    </slot>
    <slot name="reviews" v-bind="{ maxRating, scoreRating }">
      <div :class="{ 'display-none': !scoreRating }" class="base-product-card__reviews">
        <BaseRating
          v-if="typeof scoreRating === 'number'"
          class="base-product-card__rating"
          :max="maxRating"
          :score="scoreRating"
        />
        <BaseButton
          :class="{ 'display-none': !reviewsCount }"
          :aria-label="`Read ${reviewsCount} reviews about ${title}`"
          class="sf-button--pure base-product-card__reviews-count"
          data-testid="product-review-button"
          @click="$emit('click:reviews')"
        >
          ({{ reviewsCount }})
        </BaseButton>
      </div>
    </slot>
  </div>
</template>

<script>
import { defineComponent } from '@nuxtjs/composition-api';
import BaseBadge from '@shared/components/@atoms/BaseBadge/BaseBadge.vue';
import BaseButton from '@shared/components/@atoms/BaseButton/BaseButton.vue';
import BaseCircleIcon from '@shared/components/@atoms/BaseCircleIcon/BaseCircleIcon.vue';
import BaseIcon from '@shared/components/@atoms/BaseIcon/BaseIcon.vue';
import BaseImage from '@shared/components/@atoms/BaseImage/BaseImage.vue';
import BasePrice from '@shared/components/@atoms/BasePrice/BasePrice.vue';
import BaseRating from '@shared/components/@atoms/BaseRating/BaseRating.vue';
import { colorsValues as SF_COLORS } from '@shared/ui/storefront-ui/variables/colors';
import responsiveMixin from '@shared/ui/utilities/mixins/responsiveMixin';

export default defineComponent({
  name: 'BaseProductCard',
  components: {
    BasePrice,
    BaseRating,
    BaseIcon,
    BaseImage,
    BaseCircleIcon,
    BaseBadge,
    BaseButton,
  },
  mixins: [responsiveMixin],
  props: {
    image: {
      type: [Array, Object, String],
      default: '',
    },
    imageWidth: {
      type: [Number, String],
      default: null,
    },
    imageHeight: {
      type: [Number, String],
      default: null,
    },
    badgeLabel: {
      type: String,
      default: '',
    },
    badgeColor: {
      type: String,
      default: '',
    },
    colors: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    fallbackImageSrc: {
      type: String,
      default: null,
    },
    link: {
      type: [String, Object],
      default: null,
    },
    scoreRating: {
      type: [Number, Boolean],
      default: false,
    },
    reviewsCount: {
      type: [Number, Boolean],
      default: false,
    },
    maxRating: {
      type: [Number, String],
      default: 5,
    },
    regularPrice: {
      type: [Number, String],
      default: null,
    },
    specialPrice: {
      type: [Number, String],
      default: null,
    },
    showAddToCartButton: {
      type: Boolean,
      default: false,
    },
    isAddedToCart: {
      type: Boolean,
      default: false,
    },
    addToCartDisabled: {
      type: Boolean,
      default: false,
    },
    imageTag: {
      type: String,
      default: '',
    },
    nuxtImgConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isAddingToCart: false,
      openColorPicker: false,
    };
  },
  computed: {
    isBaseColors() {
      return SF_COLORS.includes(this.badgeColor.trim());
    },
    badgeColorClass() {
      return this.isBaseColors ? `${this.badgeColor.trim()}` : '';
    },
    showAddedToCartBadge() {
      return !this.isAddingToCart && this.isAddedToCart;
    },
    showBadge() {
      return this.colors.length > 5;
    },
  },
  methods: {
    onAddToCart(event) {
      event.preventDefault();
      this.isAddingToCart = true;
      const id = setTimeout(() => {
        this.isAddingToCart = false;
        clearTimeout(id);
      }, 1000);
      this.$emit('click:add-to-cart');
    },
    handleSelectedColor(colorIndex) {
      if (this.colors.length > 0) {
        this.colors.map((color, i) => {
          if (colorIndex === i) {
            this.$emit('click:colors', color);
            if (this.isMobile) {
              this.toggleColorPicker();
            }
          }
        });
      }
    },
    toggleColorPicker() {
      this.openColorPicker = !this.openColorPicker;
    },
  },
});
</script>
<style lang="scss">
.base-product-card {
  box-sizing: border-box;
  position: relative;
  z-index: var(--product-card-z-index);
  max-width: var(--product-card-max-width, 10.625rem);
  flex: 0 1 var(--product-card-max-width, 10.625rem);
  height: var(--product-card-height);
  padding: var(--product-card-padding, var(--spacer-xs));
  background-color: var(--product-card-background, var(--c-white));
  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: var(--product-card-transition);
    box-shadow: var(--product-card-box-shadow);
  }
  &:hover {
    --product-card-image-opacity: 1;
    --product-card-image-even-opacity: 1;
    --product-card-add-button-opacity: 1;
    --product-card-z-index: 1;
    --product-card-box-shadow: 0 4px 11px rgba(29, 31, 34, 0.1);
  }
  &.has-colors {
    --product-card-add-button-bottom: 0;
  }
  &:active {
    --product-card-box-shadow: 0 4px 11px rgba(29, 31, 34, 0.1);
  }
  &__link {
    display: block;
    width: 100%;
    line-height: 0;
    text-decoration: none;
    margin: var(--product-card-link-margin, 0);
    text-align: left;
  }
  &__colors {
    --color-picker-position: absolute;
    --color-picker-label-font-size: var(--font-size--base);
    --color-picker-label-font-weight: var(--font-weight--medium);
    @include lg-devices-min {
      --color-picker-position: relative;
      --color-picker-justify-content: flex-start;
      --color-picker-padding: 0;
      pointer-events: none;
    }
  }
  &__color {
    margin: var(--spacer-xs);
    pointer-events: visible;
    @include lg-devices-min {
      margin: var(--spacer-sm) var(--spacer-sm) 0 0;
    }
  }
  &__title {
    @include font(
      --product-card-title-font,
      var(--font-weight--normal),
      var(--h5-font-size),
      1.6,
      var(--font-family--secondary)
    );
    color: var(--product-card-title-color, var(--c-link));
    margin: var(--product-card-title-margin, var(--spacer-xs) 0 0 0);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }
  &__image-wrapper {
    position: relative;
    margin: var(--product-card-image-wrapper-margin, 0 auto);
  }
  &__image,
  &__picture {
    min-width: 100%;
    --image-width: var(--product-card-image-width, 100%);
    --image-height: var(--product-card-image-height, auto);
    transition: var(--product-card-image-transition, opacity 150ms ease-in-out);
    opacity: var(--product-card-image-opacity);

    &:nth-child(even) {
      position: absolute;
      top: 0;
      left: 0;
      opacity: var(--product-card-image-even-opacity, 0);
    }
  }
  &__badge {
    position: absolute;
    top: var(--product-card-badge-top, var(--spacer-xs));
    left: var(--product-card-badge-left, 0);
  }
  &__colors-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: var(--spacer-xs);
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    color: var(--c-white);
    z-index: 2;
  }
  &__add-button {
    --circle-icon-position: absolute;
    right: var(--product-card-add-button-right, 1rem);
    bottom: var(--product-card-add-button-bottom, -1.5rem);
    display: var(--product-card-add-button-display, none);
    transform: var(--product-card-add-button-transform);
    opacity: var(--product-card-add-button-opacity, 0);
    &:focus {
      --product-card-add-button-opacity: 1;
    }
    & .sf-circle-icon:hover {
      --button-box-shadow: 0px 4px 11px rgba(29, 31, 34, 0.1);
      --button-box-shadow-opacity: 0.3;
    }
  }
  &__price {
    margin: var(--product-card-margin, 0);
  }
  &__reviews {
    display: flex;
    align-items: center;
  }
  &__rating {
    display: flex;
  }
  &__reviews-count {
    margin: var(--product-card-reviews-count-margin, 0 0 0 var(--spacer-xs));
    color: var(--product-card-reviews-count-color, var(--c-text));
    text-decoration: none;
    @include font(
      --product-reviews-count-font,
      var(--font-weight--light),
      var(--font-size--sm),
      1.6,
      var(--font-family--secondary)
    );
  }
  @include lg-devices-min {
    --product-card-max-width: 15.5rem;
    --product-card-padding: var(--spacer-sm);
    --product-card-transition: box-shadow 150ms ease-in-out;
    --product-card-add-button-display: flex;
    --product-card-title-margin: var(--spacer-sm) 0 0 0;
    --product-card-margin: var(--spacer-xs) 0;
    &__colors-badge {
      margin: var(--spacer-sm) var(--spacer-sm) 0 0;
      width: 1.25rem;
      height: 1.25rem;
      box-shadow: 0 4px 4px 0px rgba(0, 0, 0, 0.25);
    }
  }
}
</style>
